import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { keysApi, SCOPES, type ApiKey, type ApiKeyCreated, type Scope } from "@/api/keys";
import { Dialog } from "@/components/ui/Dialog";
import { Check, Copy, Trash2, Plus, Eye, EyeOff, Pencil } from "lucide-react";

export const Route = createFileRoute("/app/projects/$pid/keys")({
  component: KeysPage,
});

function KeysPage() {
  const { pid } = Route.useParams();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["keys", pid], queryFn: () => keysApi.list(pid) });
  const [createOpen, setCreateOpen] = useState(false);
  const [createdKey, setCreatedKey] = useState<ApiKeyCreated | null>(null);
  const [editingKey, setEditingKey] = useState<ApiKey | null>(null);

  const revoke = useMutation({
    mutationFn: (kid: string) => keysApi.revoke(pid, kid),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["keys", pid] }),
  });

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <button className="btn-primary" onClick={() => setCreateOpen(true)}>
          <Plus size={16} className="mr-1" /> New key
        </button>
      </div>

      <div className="card overflow-x-auto p-0">
        <table className="w-full text-sm">
          <thead className="bg-cream text-left text-xs uppercase tracking-wide text-ink/55">
            <tr>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Key</th>
              <th className="px-4 py-3">Scopes</th>
              <th className="px-4 py-3">
                <span
                  title="Most recent request, based on the last 90 days of usage. Older activity isn't retained."
                  className="cursor-help underline decoration-dotted underline-offset-2"
                >
                  Last used
                </span>
              </th>
              <th className="px-4 py-3">Created</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={6} className="px-4 py-6 text-ink/55">Loading…</td></tr>
            ) : data && data.length > 0 ? (
              data.map((k) => {
                const isRevoked = !!k.revoked_at;
                return (
                <tr key={k.id} className={`border-t border-ink/8 ${isRevoked ? "text-ink/45" : ""}`}>
                  <td className="px-4 py-3 font-medium">
                    {k.name}
                    {isRevoked && (
                      <span className="ml-2 rounded bg-ink/4 px-1.5 py-0.5 text-xs font-normal uppercase text-ink/55">
                        Revoked
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <SecretCell apiKey={k} />
                  </td>
                  <td className="px-4 py-3">
                    {k.scopes && k.scopes.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {k.scopes.map((s) => (
                          <code key={s} className="rounded bg-ink/4 px-1.5 py-0.5 font-mono text-xs">{s}</code>
                        ))}
                      </div>
                    ) : (
                      <span className="text-xs text-ink/45">all</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-ink/55">{k.last_used_at ? new Date(k.last_used_at).toLocaleString() : "never"}</td>
                  <td className="px-4 py-3 text-ink/55">{new Date(k.created_at).toLocaleDateString()}</td>
                  <td className="px-4 py-3 text-right">
                    {!isRevoked && (
                      <div className="inline-flex items-center gap-3">
                        <button
                          onClick={() => setEditingKey(k)}
                          className="text-ink/65 hover:text-ink"
                          title="Edit"
                        >
                          <Pencil size={16} />
                        </button>
                        <button
                          onClick={() => { if (confirm(`Revoke key "${k.name}"? This is irreversible.`)) revoke.mutate(k.id); }}
                          className="text-red-600 hover:text-red-700"
                          title="Revoke"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
                );
              })
            ) : (
              <tr><td colSpan={6} className="px-4 py-6 text-ink/55">No API keys yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <CreateKeyDialog
        pid={pid}
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(k) => setCreatedKey(k)}
      />
      <SecretReveal apiKey={createdKey} onClose={() => setCreatedKey(null)} />
      <EditKeyDialog
        pid={pid}
        apiKey={editingKey}
        onClose={() => setEditingKey(null)}
      />
    </div>
  );
}

// Per-row reveal + copy. Secret is fetched as part of the list response so
// toggling doesn't issue a network call. Falls back to the prefix-only
// display for legacy keys (no encrypted_secret on the row).
function SecretCell({ apiKey }: { apiKey: ApiKey }) {
  const [shown, setShown] = useState(false);
  const [copied, setCopied] = useState(false);

  if (!apiKey.secret) {
    return <code className="font-mono text-xs text-ink/55">{apiKey.prefix}…</code>;
  }

  async function copy() {
    if (!apiKey.secret) return;
    try {
      await navigator.clipboard.writeText(apiKey.secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked — silent no-op */
    }
  }

  return (
    <div className="inline-flex items-center gap-2">
      <code className="font-mono text-xs">
        {shown ? apiKey.secret : `${apiKey.prefix}${"•".repeat(20)}`}
      </code>
      <button
        type="button"
        className="text-ink/55 hover:text-ink"
        onClick={() => setShown((s) => !s)}
        title={shown ? "Hide" : "Reveal"}
        aria-label={shown ? "Hide secret" : "Reveal secret"}
      >
        {shown ? <EyeOff size={14} /> : <Eye size={14} />}
      </button>
      <button
        type="button"
        className={copied ? "text-green-600" : "text-ink/55 hover:text-ink"}
        onClick={copy}
        title={copied ? "Copied" : "Copy"}
        aria-label="Copy secret"
      >
        {copied ? <Check size={14} /> : <Copy size={14} />}
      </button>
    </div>
  );
}

function CreateKeyDialog({
  pid, open, onOpenChange, onCreated,
}: {
  pid: string; open: boolean; onOpenChange: (b: boolean) => void;
  onCreated: (k: ApiKeyCreated) => void;
}) {
  const [name, setName] = useState("");
  const [origins, setOrigins] = useState("");
  // null = full access (no scope guard); set = narrowed.
  const [scopes, setScopes] = useState<Set<Scope>>(new Set());
  const [restrictScopes, setRestrictScopes] = useState(false);
  const qc = useQueryClient();

  const m = useMutation({
    mutationFn: () => {
      const list = origins.split(",").map((s) => s.trim()).filter(Boolean);
      return keysApi.create(pid, name.trim(), {
        origin_allowlist: list.length ? list : undefined,
        scopes: restrictScopes ? Array.from(scopes) : undefined,
      });
    },
    onSuccess: async (k) => {
      await qc.invalidateQueries({ queryKey: ["keys", pid] });
      onOpenChange(false);
      onCreated(k);
      setName("");
      setOrigins("");
      setScopes(new Set());
      setRestrictScopes(false);
    },
  });

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="New API key"
      description="You can reveal and copy the secret again later from the keys list."
    >
      <form onSubmit={(e) => { e.preventDefault(); if (name.trim()) m.mutate(); }} className="space-y-4">
        <div>
          <label className="label">Name</label>
          <input className="input mt-1" autoFocus required value={name} onChange={(e) => setName(e.target.value)} placeholder="prod-server, staging, …" />
        </div>
        <div>
          <label className="label">Origin allowlist (optional)</label>
          <input className="input mt-1" value={origins} onChange={(e) => setOrigins(e.target.value)} placeholder="https://app.example.com, https://example.com" />
          <p className="mt-1 text-xs text-ink/55">Comma-separated. Empty = no Origin restriction.</p>
        </div>
        <div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={restrictScopes} onChange={(e) => setRestrictScopes(e.target.checked)} />
            Restrict scopes
          </label>
          {restrictScopes && (
            <div className="mt-2 space-y-1 rounded-md border border-ink/8 p-3 text-sm">
              {SCOPES.map((s) => (
                <label key={s} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={scopes.has(s)}
                    onChange={(e) => {
                      const next = new Set(scopes);
                      if (e.target.checked) next.add(s); else next.delete(s);
                      setScopes(next);
                    }}
                  />
                  <code className="font-mono text-xs">{s}</code>
                </label>
              ))}
              <p className="mt-1 text-xs text-ink/55">
                Selected scopes are the only endpoints this key can call. Unchecked ⇒ full access.
              </p>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={() => onOpenChange(false)} className="btn-secondary">Cancel</button>
          <button type="submit" className="btn-primary" disabled={m.isPending}>{m.isPending ? "Creating…" : "Create"}</button>
        </div>
      </form>
    </Dialog>
  );
}

function EditKeyDialog({
  pid, apiKey, onClose,
}: {
  pid: string; apiKey: ApiKey | null; onClose: () => void;
}) {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [origins, setOrigins] = useState("");
  const [scopes, setScopes] = useState<Set<Scope>>(new Set());
  const [restrictScopes, setRestrictScopes] = useState(false);
  const [initialised, setInitialised] = useState(false);

  // Hydrate when the dialog opens with a new key; reset when it closes.
  if (apiKey && !initialised) {
    setName(apiKey.name);
    setOrigins((apiKey.origin_allowlist ?? []).join(", "));
    setScopes(new Set(apiKey.scopes ?? []));
    setRestrictScopes((apiKey.scopes ?? null) !== null);
    setInitialised(true);
  } else if (!apiKey && initialised) {
    setInitialised(false);
  }

  const m = useMutation({
    mutationFn: () => {
      if (!apiKey) throw new Error("no key");
      const list = origins.split(",").map((s) => s.trim()).filter(Boolean);
      return keysApi.update(pid, apiKey.id, {
        name: name.trim(),
        origin_allowlist: list.length ? list : null,
        scopes: restrictScopes ? Array.from(scopes) : null,
      });
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["keys", pid] });
      onClose();
    },
  });

  return (
    <Dialog
      open={!!apiKey}
      onOpenChange={(o) => !o && onClose()}
      title="Edit API key"
      description="Changes apply on the next request (caches refresh in <60s)."
    >
      <form onSubmit={(e) => { e.preventDefault(); if (name.trim()) m.mutate(); }} className="space-y-4">
        <div>
          <label className="label">Name</label>
          <input className="input mt-1" required value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div>
          <label className="label">Origin allowlist</label>
          <input className="input mt-1" value={origins} onChange={(e) => setOrigins(e.target.value)} placeholder="https://app.example.com, https://example.com" />
          <p className="mt-1 text-xs text-ink/55">Comma-separated. Empty = no Origin restriction.</p>
        </div>
        <div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={restrictScopes} onChange={(e) => setRestrictScopes(e.target.checked)} />
            Restrict scopes
          </label>
          {restrictScopes && (
            <div className="mt-2 space-y-1 rounded-md border border-ink/8 p-3 text-sm">
              {SCOPES.map((s) => (
                <label key={s} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={scopes.has(s)}
                    onChange={(e) => {
                      const next = new Set(scopes);
                      if (e.target.checked) next.add(s); else next.delete(s);
                      setScopes(next);
                    }}
                  />
                  <code className="font-mono text-xs">{s}</code>
                </label>
              ))}
              <p className="mt-1 text-xs text-ink/55">
                Selected scopes are the only endpoints this key can call. Unchecked ⇒ full access.
              </p>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
          <button type="submit" className="btn-primary" disabled={m.isPending}>{m.isPending ? "Saving…" : "Save"}</button>
        </div>
      </form>
    </Dialog>
  );
}

function SecretReveal({ apiKey, onClose }: { apiKey: ApiKey & { secret?: string | null } | null; onClose: () => void }) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    if (!apiKey?.secret) return;
    try {
      await navigator.clipboard.writeText(apiKey.secret);
      setCopied(true);
      // Short flash — long enough to register, short enough that
      // re-clicking shows the transition again. ~1.5s is the standard.
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard API can be blocked (insecure origin, denied perm).
      // Silently no-op — user can still select+copy from the box.
    }
  }

  return (
    <Dialog
      open={!!apiKey}
      onOpenChange={(o) => !o && onClose()}
      title="API key created"
      description="Copy the secret to use it now — you can also reveal it again from the keys list."
    >
      {apiKey?.secret && (
        <div className="space-y-4">
          <div className="rounded-md bg-ink-500 p-3 font-mono text-xs text-green-400 break-all">
            {apiKey.secret}
          </div>
          <button
            className={`btn-secondary w-full transition-colors ${
              copied ? "bg-green-50 text-green-700 border-green-200 " : ""
            }`}
            onClick={copy}
          >
            {copied
              ? <><Check size={14} className="mr-1" /> Copied!</>
              : <><Copy size={14} className="mr-1" /> Copy to clipboard</>}
          </button>
          <div className="flex justify-end">
            <button className="btn-primary" onClick={onClose}>Done</button>
          </div>
        </div>
      )}
    </Dialog>
  );
}
